import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { projectListQueryOptions } from '@/entities/project'
import { cn } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { EmptyState } from '@/shared/ui/empty-state'
import { Field } from '@/shared/ui/field'
import { Icon } from '@/shared/ui/icon'
import { PageHeader } from '@/shared/ui/page-header'
import {
  currentMonthPeriod,
  PeriodPicker,
  type PeriodValue,
  periodValid,
} from '@/shared/ui/period-picker'
import { Select } from '@/shared/ui/select'
import { type ReportKind, ReportSkeleton, ReportView } from '@/widgets/report-view'

interface ReportsSearch {
  // Разрез: team — дефолт, в URL живёт только project.
  view?: 'project'
  id?: string
  // Раскрытая строка сотрудника в командном разрезе (личная разбивка) — в URL,
  // чтобы переход из проектного отчёта открывал именно его строку.
  open?: string
  from?: string
  to?: string
}

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/

export const Route = createFileRoute('/reports')({
  component: ReportsPage,
  head: () => ({ meta: [{ title: 'Отчёты · STIT Task' }] }),
  // Весь стейт экрана — в URL (шаринг ссылки на отчёт); дефолты (разрез team,
  // первый проект, текущий месяц) в URL не пишутся — чистый /reports.
  validateSearch: (s: Record<string, unknown>): ReportsSearch => ({
    ...(s.view === 'project' ? { view: s.view } : {}),
    ...(typeof s.id === 'string' && s.id ? { id: s.id } : {}),
    ...(typeof s.open === 'string' && s.open ? { open: s.open } : {}),
    ...(typeof s.from === 'string' && DAY_RE.test(s.from) ? { from: s.from } : {}),
    ...(typeof s.to === 'string' && DAY_RE.test(s.to) ? { to: s.to } : {}),
  }),
})

const VIEWS: { value: ReportKind; label: string }[] = [
  { value: 'team', label: 'Команда' },
  { value: 'project', label: 'По проекту' },
]

// Таб — mono-подчёркивание bg-полосой (не border-color: глобальный unlayered
// сброс `* { border-color }` бьёт border-утилиты), как фильтры «Моих задач».
function ViewTab({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: string
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'relative pb-1.5 font-mono text-xs uppercase tracking-wide transition-colors',
        active ? 'text-text-primary' : 'text-text-tertiary hover:text-text-secondary',
      )}
    >
      {children}
      {active && (
        <span
          aria-hidden
          className="absolute inset-x-0 bottom-0 h-0.5 rounded-pill bg-text-primary"
        />
      )}
    </button>
  )
}

function Header() {
  return (
    <PageHeader
      title="Отчёты"
      eyebrow="Время"
      actions={
        // Экспорта в API пока нет — кнопка закладывается задизейбленной
        // (docs/design/screens/reports.md §Экспорт).
        <span title="Скоро — экспорта в API пока нет">
          <Button variant="secondary" size="sm" disabled>
            <Icon name="report" size={14} />
            CSV · скоро
          </Button>
        </span>
      }
    />
  )
}

function ReportsPage() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const view: ReportKind = search.view ?? 'team'
  const defaultPeriod = currentMonthPeriod()
  const period: PeriodValue = {
    from: search.from ?? defaultPeriod.from,
    to: search.to ?? defaultPeriod.to,
  }

  // include_archived: отчёты строят и за прошлые периоды, когда проект уже в
  // архиве — архивные в селекторе с пометкой.
  const projectsQ = useQuery(projectListQueryOptions(true))

  const objectId = view === 'project' ? (search.id ?? projectsQ.data?.[0]?.id) : undefined

  // Пересборка всего search-объекта (exactOptionalPropertyTypes): дефолты
  // выпадают из URL, явный выбор — остаётся.
  const buildSearch = (next: {
    view: ReportKind
    id?: string | undefined
    open?: string | undefined
    period: PeriodValue
  }): ReportsSearch => ({
    ...(next.view === 'project' ? { view: 'project' as const } : {}),
    ...(next.view === 'project' && next.id ? { id: next.id } : {}),
    ...(next.view === 'team' && next.open ? { open: next.open } : {}),
    ...(next.period.from === defaultPeriod.from && next.period.to === defaultPeriod.to
      ? {}
      : { from: next.period.from, to: next.period.to }),
  })

  // Смена разреза сбрасывает объект: у командного его нет, у проектного —
  // дефолт (первый проект). Период сохраняется.
  const setView = (v: ReportKind) => void navigate({ search: buildSearch({ view: v, period }) })
  const setId = (id: string) => void navigate({ search: buildSearch({ view, id, period }) })
  const setPeriod = (p: PeriodValue) =>
    void navigate({
      search: buildSearch({ view, id: search.id, open: search.open, period: p }),
      replace: true,
    })
  // Повторный клик по раскрытой строке её сворачивает.
  const toggleMember = (id: string) =>
    void navigate({
      search: buildSearch({
        view,
        open: search.open === id ? undefined : id,
        period,
      }),
      replace: true,
    })

  return (
    <div className="container-x px-4 py-8 sm:px-6 sm:py-10">
      <Header />

      <div className="flex items-center gap-5 border-b border-border-subtle pb-px">
        {VIEWS.map((v) => (
          <ViewTab key={v.value} active={view === v.value} onClick={() => setView(v.value)}>
            {v.label}
          </ViewTab>
        ))}
      </div>

      <div className="mt-6 flex flex-wrap items-start gap-x-10 gap-y-6">
        {view === 'project' && (
          <div className="w-full max-w-80">
            <Field label="Проект">
              {(p) => (
                <Select
                  {...p}
                  value={objectId ?? ''}
                  onChange={setId}
                  disabled={projectsQ.isPending || projectsQ.isError}
                  options={(projectsQ.data ?? []).map((pr) => ({
                    value: pr.id,
                    label: pr.archived ? `${pr.name} (архивный)` : pr.name,
                  }))}
                />
              )}
            </Field>
          </div>
        )}
        <PeriodPicker value={period} onChange={setPeriod} />
      </div>

      <div className="mt-10 max-w-[880px]">
        <ReportArea
          view={view}
          objectId={objectId}
          period={period}
          openId={search.open}
          onToggleMember={toggleMember}
          dictPending={view === 'project' && projectsQ.isPending}
          dictError={view === 'project' && projectsQ.isError}
          onRetryDict={() => {
            void projectsQ.refetch()
          }}
        />
      </div>
    </div>
  )
}

function ReportArea({
  view,
  objectId,
  period,
  openId,
  onToggleMember,
  dictPending,
  dictError,
  onRetryDict,
}: {
  view: ReportKind
  objectId: string | undefined
  period: PeriodValue
  openId: string | undefined
  onToggleMember: (id: string) => void
  dictPending: boolean
  dictError: boolean
  onRetryDict: () => void
}) {
  if (!periodValid(period)) {
    return (
      <p className="px-1 py-8 text-sm text-text-tertiary">
        Исправьте период, чтобы построить отчёт.
      </p>
    )
  }
  if (dictPending) return <ReportSkeleton />
  if (dictError) {
    return (
      <div className="flex flex-col items-center gap-4 rounded-card bg-bg-surface py-14 text-center">
        <p className="text-text-secondary">Не удалось загрузить проекты</p>
        <Button variant="secondary" size="sm" onClick={onRetryDict}>
          Повторить
        </Button>
      </div>
    )
  }
  if (view === 'project' && !objectId) {
    return (
      <EmptyState
        icon={<Icon name="report" size={40} />}
        title="Проектов пока нет"
        description="Отчёт появится, когда будет создан первый проект."
      />
    )
  }
  return (
    <ReportView
      kind={view}
      objectId={objectId}
      from={period.from}
      to={period.to}
      openId={openId}
      onToggleMember={onToggleMember}
    />
  )
}
